import type { TFunction } from 'i18next';
import { FormEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { GoogleSignInButton } from '../../components/GoogleSignInButton';
import { PasswordInput } from '../../components/PasswordInput';
import { useFeatures } from '../../hooks/useFeatures';
import { APIError, idpApi } from '../../services/idp-api';

function getSignUpErrorMessage(error: unknown, t: TFunction<'idp'>): string {
  if (!(error instanceof APIError)) {
    return t('signUp.errors.default');
  }

  if (error.code === 'EMAIL_EXISTS') {
    return t('signUp.errors.emailExists');
  }

  if (error.code === 'PASSWORD_COMPROMISED') {
    return t('signUp.errors.passwordCompromised');
  }

  if (error.code === 'VALIDATION_ERROR') {
    return t('signUp.errors.validationError');
  }

  return error.message;
}

export function SignUpPage() {
  const { t } = useTranslation('idp');
  const { google: googleEnabled } = useFeatures();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const interactionId = searchParams.get('interaction')?.trim() ?? '';

  const isFormInvalid = useMemo(
    () => name.trim().length === 0 || email.trim().length === 0 || password.length < 12,
    [email, name, password]
  );

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrorMessage(null);
    setSuccessMessage(null);
    setIsSubmitting(true);

    try {
      const request: { name: string; email: string; password: string; interactionId?: string } = {
        name: name.trim(),
        email: email.trim(),
        password,
      };
      if (interactionId !== '') {
        request.interactionId = interactionId;
      }
      await idpApi.signup(request);

      setSuccessMessage(t('signUp.successMessage'));
      setPassword('');
    } catch (error) {
      setErrorMessage(getSignUpErrorMessage(error, t));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Box sx={{ minHeight: '70vh', display: 'grid', placeItems: 'center' }}>
      <Card sx={{ width: '100%', maxWidth: 520 }}>
        <CardContent>
          <Stack
            spacing={3}
            component="form"
            onSubmit={(event) => {
              void handleSubmit(event);
            }}
          >
            <Stack spacing={1}>
              <Typography variant="h5" component="h1" fontWeight={700}>
                {t('signUp.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('signUp.subtitle')}
              </Typography>
            </Stack>

            {successMessage !== null ? <Alert severity="success">{successMessage}</Alert> : null}
            {errorMessage !== null ? <Alert severity="error">{errorMessage}</Alert> : null}

            {googleEnabled ? (
              <>
                <GoogleSignInButton flowId={interactionId} label={t('signUp.signUpWithGoogle')} />
                <Divider>
                  <Typography variant="body2" color="text.secondary">
                    {t('signUp.orSignUpWithEmail')}
                  </Typography>
                </Divider>
              </>
            ) : null}

            <TextField
              label={t('signUp.nameLabel')}
              autoComplete="name"
              value={name}
              onChange={(event) => {
                setName(event.target.value);
              }}
              required
              fullWidth
            />

            <TextField
              label={t('signUp.emailLabel')}
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
              required
              fullWidth
            />

            <PasswordInput
              label={t('signUp.passwordLabel')}
              autoComplete="new-password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
              }}
              required
              fullWidth
            />

            <Button type="submit" variant="contained" disabled={isSubmitting || isFormInvalid}>
              {isSubmitting ? t('signUp.submittingButton') : t('signUp.submitButton')}
            </Button>

            <Typography variant="body2" color="text.secondary">
              {t('signUp.alreadyHaveAccount')}{' '}
              <Link
                component={RouterLink}
                to={
                  interactionId !== ''
                    ? `/auth/signin?interaction=${encodeURIComponent(interactionId)}`
                    : '/auth/signin'
                }
                underline="hover"
              >
                {t('signUp.signInLink')}
              </Link>
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
